import React, {Component, ReactNode} from 'react'
import FacilitiesRepository from '../facilities/FacilitiesRepository'
import {Nullable} from '../../app/TypeHelpers'
import HolidaysRepository from '../HolidaysRepository'
import {
    NewDurationRentalFacilityReservation,
    NewOvernightRentalFacilityReservation,
    ReservationOption,
    ReserveFacilityModel,
    ReserveFacilityModelDataSetObserver,
    ReserveFacilityModelNewReservationObserver,
} from './ReserveFacilityModel'
import ResidentReserveFacilityForm from './ResidentReserveFacilityForm'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import ReservationsRepository from './ReservationsRepository'
import FacilityForReservation from '../facilities/FacilityForReservation'
import {PrimaryStyledButton} from '../../styledComponents/Button'
import NatRoomNumberRepo from '../../__NoAutomatedTests/components/roomNumber/repository/NatRoomNumberRepo'
import NatRidgeRepo from '../../__NoAutomatedTests/components/ridge/repository/NatRidgeRepo'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {HttpError} from '../../network/errors/HttpError'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {Messages} from '../../Messages'
import {SelectedUser} from '../../__NoAutomatedTests/components/common/resident/NatResidentSelectedUser'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {Location} from 'react-router-dom'

export interface ResidentReserveFacilityInputPageLocationState {
    previousPageUrl: string

    duration?: {
        startDate: Nullable<string>
        startTime: Nullable<string>
        endDate: Nullable<string>
        endTime: Nullable<string>,
    }

    overnight?: {
        startDate: Nullable<string>
        stayCount: Nullable<number>,
    }
}

interface LocationState extends ResidentReserveFacilityInputPageLocationState {
    errorResponseBody: Nullable<ErrorResponseBody>
    stayAvailable: Nullable<boolean>
    reserveInfo: NewOvernightRentalFacilityReservation | NewDurationRentalFacilityReservation
}

interface Props {
    condominiumCd: string
    facilityId: number
    facilitiesRepo: FacilitiesRepository
    reservationsRepo: ReservationsRepository
    holidaysRepo: HolidaysRepository
    roomNumberRepo: NatRoomNumberRepo
    ridgeRepo: NatRidgeRepo
    eventEmitter: EventEmitterExResident
    reservationModel: ReserveFacilityModel
    currentDateTimeRepo: NatCurrentDateTimeRepo

    location: Location<LocationState>
}

interface State {
    isEnableToRegister: boolean
    errorResponseBody: Nullable<ErrorResponseBody>

    reservationStartDate: Nullable<string>
    reservationStartTime: Nullable<string>
    reservationEndDate: Nullable<string>
    reservationEndTime: Nullable<string>
    reservationStayCount: Nullable<number>
    reservationTitle: Nullable<string>
    sharedUseRoomNumberList: SelectedUser[]
    reservationOptions: ReservationOption[]
}

class ResidentReserveFacilityInputPage extends Component<Props & ResidentPagePropsSet, State>
    implements ReserveFacilityModelDataSetObserver, ReserveFacilityModelNewReservationObserver {

    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)
        let startDate: Nullable<string> = null
        let startTime: Nullable<string> = null
        let endDate: Nullable<string> = null
        let endTime: Nullable<string> = null
        let stayCount: Nullable<number> = null
        let reservationTitle: Nullable<string> = null
        let reservationOptions: ReservationOption[] = []
        let sharedUseRoomNumberList: SelectedUser[] = []


        let locationStateErrorResponseBody: Nullable<ErrorResponseBody> = null

        if (this.props.location.state) {
            const {overnight, duration, errorResponseBody, reserveInfo, stayAvailable} = this.props.location.state
            if (reserveInfo !== undefined) {
                if (stayAvailable) {
                    const reserveInfoOvernight: NewOvernightRentalFacilityReservation =
                        reserveInfo as NewOvernightRentalFacilityReservation
                    startDate = reserveInfoOvernight.startDate
                    stayCount = reserveInfoOvernight.stayCount
                    reservationTitle = reserveInfoOvernight.reservationTitle
                    reservationOptions = reserveInfoOvernight.options
                    sharedUseRoomNumberList = reserveInfoOvernight.sharedUseRoomNumberList
                } else {
                    const reserveInfoDuration: NewDurationRentalFacilityReservation =
                        reserveInfo as NewDurationRentalFacilityReservation
                    startDate = reserveInfoDuration.startDate
                    startTime = reserveInfoDuration.startTime
                    endDate = reserveInfoDuration.endDate
                    endTime = reserveInfoDuration.endTime
                    reservationTitle = reserveInfoDuration.reservationTitle
                    reservationOptions = reserveInfoDuration.options
                    sharedUseRoomNumberList = reserveInfoDuration.sharedUseRoomNumberList
                }
            }
            if (overnight !== undefined) {
                startDate = overnight.startDate
                stayCount = overnight.stayCount
            }
            if (duration !== undefined) {
                startDate = duration.startDate
                startTime = duration.startTime
                endDate = duration.endDate
                endTime = duration.endTime
            }
            if (errorResponseBody !== undefined) {
                locationStateErrorResponseBody = errorResponseBody
            }
        }

        props.reservationModel
            .addDataSetStateObserver(this)
            .addNewReservationObserver(this)
        this.state = {
            isEnableToRegister: false,
            errorResponseBody: locationStateErrorResponseBody,
            reservationStartDate: startDate,
            reservationStartTime: startTime,
            reservationEndDate: endDate,
            reservationEndTime: endTime,
            reservationStayCount: stayCount,
            reservationTitle: reservationTitle,
            reservationOptions: reservationOptions,
            sharedUseRoomNumberList: sharedUseRoomNumberList,
        }
        this.handleOnClickReserveButton = this.handleOnClickReserveButton.bind(this)
    }

    handleOnClickReserveButton(): void {
        this.props.reservationModel.build()
    }

    onDataSetStateChange(completeToCreate: boolean): void {
        if (this.state.isEnableToRegister === completeToCreate) {
            return
        }
        this.setState({isEnableToRegister: completeToCreate})
    }

    async onBuildOvernightRentalFacilityReservation(
        newReservation: NewOvernightRentalFacilityReservation,
        facilityData: FacilityForReservation,
    ): Promise<void> {
        const {condominiumCd, facilityId, reservationsRepo, navigate, location} = this.props
        const {previousPageUrl} = location.state

        this.setState({
            errorResponseBody: null,
        }, async () => {
            await this.props.eventEmitter.showLoading()

            try {
                const returnValue = await NatAsyncCallFunction(
                    reservationsRepo.prepareReserveFacilityForOvernight.bind(
                        reservationsRepo, condominiumCd, facilityId, newReservation,
                    ),
                    navigate,
                )
                if (returnValue !== null) {
                    this.props.navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reserve/confirm`, {
                        state: {
                            previousPageUrl: previousPageUrl,
                            stayAvailable: true,
                            facilityInfo: facilityData,
                            reserveInfo: newReservation,
                        },
                    })
                }
            } catch (e) {
                const httpError: HttpError = e as HttpError
                this.setState({
                    errorResponseBody: {
                        ...httpError.responseBody,
                        requestError: {
                            ...httpError.responseBody.requestError,
                            title: Messages.EOPCR0001.toString('施設予約'),
                        },
                    },
                })
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        })
    }

    async onBuildDurationRentalFacilityReservation(
        newReservation: NewDurationRentalFacilityReservation,
        facilityData: FacilityForReservation,
    ): Promise<void> {
        const {condominiumCd, facilityId, reservationsRepo, navigate, location} = this.props
        const {previousPageUrl} = location.state

        this.setState({
            errorResponseBody: null,
        }, async () => {
            try {
                await this.props.eventEmitter.showLoading()

                const returnValue = await NatAsyncCallFunction(
                    reservationsRepo.prepareReserveFacilityForDuration.bind(
                        reservationsRepo, condominiumCd, facilityId, newReservation), navigate,
                )
                if (returnValue !== null) {
                    this.props.navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reserve/confirm`, {
                        state: {
                            previousPageUrl: previousPageUrl,
                            stayAvailable: false,
                            facilityInfo: facilityData,
                            reserveInfo: newReservation,
                        },
                    })
                }
            } catch (e) {
                const httpError: HttpError = e as HttpError
                this.setState({
                    errorResponseBody: {
                        ...httpError.responseBody,
                        requestError: {
                            ...httpError.responseBody.requestError,
                            title: Messages.EOPCR0001.toString('施設予約'),
                        },
                    },
                })
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        })
    }

    render(): ReactNode {
        const {
            reservationEndDate, reservationEndTime,
            reservationStartDate, reservationStartTime,
            reservationStayCount,
            reservationTitle, reservationOptions, sharedUseRoomNumberList,
        } = this.state

        let previousPageUrl: string = `/resident/condominiums/${this.props.condominiumCd}/reserve-facility-top`
        if (this.props.location.state) {
            previousPageUrl = this.props.location.state.previousPageUrl
        }
        return (
            <>
                <PlainBackLink to={previousPageUrl}>戻る</PlainBackLink>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <ResidentReserveFacilityForm
                    reservationId={null}
                    {...this.props}
                    eventEmitter={this.props.eventEmitter}
                    reservationStartDate={reservationStartDate}
                    reservationStartTime={reservationStartTime}
                    reservationEndDate={reservationEndDate}
                    reservationEndTime={reservationEndTime}
                    reservationStayCount={reservationStayCount}
                    reservationTitle={reservationTitle}
                    sharedUseRoomNumberList={sharedUseRoomNumberList}
                    reservationOptions={reservationOptions}
                    currentDateTimeRepo={this.props.currentDateTimeRepo}
                    errorResponseBody={this.state.errorResponseBody}
                    renderSubmitButton={() =>
                        <PrimaryStyledButton
                            onClick={this.handleOnClickReserveButton}
                            disabled={!this.state.isEnableToRegister}>
                            予約内容を確認する
                        </PrimaryStyledButton>
                    }
                />
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createResidentPage(ResidentReserveFacilityInputPage)
